import React, { useState, useEffect, useMemo } from 'react';
import { useAsync } from 'react-async-hook';
import Widget from './Widget';
import sumupServiceFactory from '../services/sumupService';
import epayServiceFactory from '../services/epayService';
import sumupSubscriptionsServiceFactory from '../services/sumupSubscriptionsService';
import merchantWalletServiceFactory from '../services/merchantWalletService';
import WidgetConfigContext from './WidgetConfigContext';
import crossWindow from '../utils/crossWindow';
import i18n from '../i18n';
import { businessAccountValidationSchema, validateParams } from '../utils/validation';
import { notifyParent } from '../utils/notifyParent';
import { MESSAGE_TYPES } from '../../shared/constants';
import queryParameters from '../utils/queryParameters';
import { parseJwt } from '../utils/parseToken';
import { setUser } from '../utils/monitoring';
import { request } from '../services/api';

const WidgetContainer = () => {
	const [ legalInfo, setLegalInfo ] = useState();
	const [ widgetOffset, setWidgetOffset ] = useState({ offset: 0, innerHeight: 0 });
	const [ businessAccount, setBusinessAccount ] = useState({ enabled: false, firstName: '', lastName: '', iban: '' });
	const [ customerReference, setCustomerReference ] = useState();

	useEffect(() => {
		crossWindow.addMessageListener(MESSAGE_TYPES.LEGAL_INFO, (data) => {
			setLegalInfo(data);
		});
		crossWindow.addMessageListener(MESSAGE_TYPES.ALIGN_OFFSET, (data) => {
			setWidgetOffset(data);
		});
		crossWindow.addMessageListener(MESSAGE_TYPES.BUSINESS_ACCOUNT, (data) => {
			const { success, data: validatedData, error } = businessAccountValidationSchema.safeParse(data);
			if (success) {
				const { businessAccountDirectDebitsEnabled, businessAccountCompanyName, businessAccountIban } = validatedData;
				setBusinessAccount({
					enabled: businessAccountDirectDebitsEnabled,
					companyName: businessAccountCompanyName,
					iban: businessAccountIban
				});
			} else {
				console.error(error, 'Error parsing business account');
			}
		});
	}, []);

	const {
		type,
		token, planId,
		email, company,
		country, state, city, zip, addressLine1, addressLine2,
		phone, vatNumber, firstName, lastName,
		regionId, regionName,
		_debug,
		showBillingDetails,
		showCoupon, couponCode,
		showVatNumber,
		showCardZipCode,
		showDirectDebit,
		showBusinessAccount,
		showPayout,
		showSuPaymentMethod,
		showSUBA,
		legalEntity,
		redirectUrl,
		invoiceInfo,
		showEditMode,
		promotionId,
		discountId,
	} = queryParameters;

	useEffect(() => {
		const { customerReference = '', accountId = '' } = parseJwt(token) || {};
		if (customerReference && accountId) {
			setUser({ id: customerReference, accountId });
			setCustomerReference(customerReference);
		}
	}, [token]);

	_debug && i18n.debug.enable();

	const getConfig = useAsync(setupServices, [token]);

	const { valid, error } = validateParams(queryParameters);

	if (!valid && error) {
		Object.assign(getConfig, { error, status: 'error' });
		Object.freeze((getConfig));
	}

	const billingDetails = {
		email, company, country, state, city, zip,
		addressLine1, addressLine2,
		phone, vatNumber, firstName, lastName, invoiceInfo,
		regionId, regionName,
	};

	const availablePaymentMethods = useMemo(() => ({
		creditCard: true,
		directDebit: showDirectDebit,
		businessAccount: showBusinessAccount && businessAccount.iban && businessAccount.enabled,
		suPayout: showPayout,
		suPaymentMethods: showSuPaymentMethod,
		suBusinessAccount: showSUBA,
	}), [
		showDirectDebit,
		showBusinessAccount,
		businessAccount.iban,
		businessAccount.enabled,
		showPayout,
		showSuPaymentMethod,
		showSUBA,
	]);

	return (
		<WidgetConfigContext.Provider
			value={{ getConfig }}
		>
			<Widget
				type={type}
				showBillingDetails={showBillingDetails}
				showCardZipCode={showCardZipCode}
				showVatNumber={showVatNumber}
				showCoupon={showCoupon}
				billingDetails={billingDetails}
				planId={planId}
				availablePaymentMethods={availablePaymentMethods}
				legalEntity={legalEntity}
				legalInfo={legalInfo}
				businessAccount={businessAccount}
				onSuccess={(info) => notifyParent({ message: MESSAGE_TYPES.ON_SUCCESS, payload: info })}
				onError={(info) => notifyParent({ message: MESSAGE_TYPES.ON_ERROR, payload: info })}
				redirectUrl={redirectUrl}
				couponCode={couponCode}
				widgetOffset={widgetOffset}
				showEditMode={showEditMode}
				customerReference={customerReference}
				promotionId={promotionId}
				discountId={discountId}
			/>
		</WidgetConfigContext.Provider>
	);
};

async function setupServices(token) {
	const { sumupBaseUrl } = await request.get('/widget/config');
	const sumupService = sumupServiceFactory({ sumupBaseUrl });
	const epayService = epayServiceFactory({ token });
	const sumupSubscriptionsService = sumupSubscriptionsServiceFactory({ token });
	const merchantWalletService = merchantWalletServiceFactory({ token });

	return { sumupService, epayService, sumupSubscriptionsService, merchantWalletService };
}

export default WidgetContainer;
